import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import { CRE8_APPS } from '../../data/cre8Apps';

/* A look at the kind of software that comes out of this process.
 *
 * Built as a stage plus a rail rather than a row of equal cards: one product
 * gets the full frame and its own copy, the rail underneath keeps all of them
 * in view and doubles as the navigation. The stage advances on its own so the
 * section shows movement without asking for a click, and stops the moment a
 * visitor takes over — hover, focus, arrow or thumbnail.
 */

const AUTOPLAY_MS = 5200;

const COPY = {
  de: {
    label: 'Werkschau',
    title: 'Ein Eindruck davon, was',
    highlight: 'hier entsteht',
    description:
      'Jedes dieser Produkte hat als Prototyp angefangen. Ein Querschnitt, der zeigt, welche Art von Software aus diesem Prozess kommt.',
    live: 'Live',
    building: 'In Arbeit',
    open: 'Ansehen',
    prev: 'Vorheriges Produkt',
    next: 'Nächstes Produkt',
  },
  en: {
    label: 'Showcase',
    title: 'An idea of what',
    highlight: 'gets built here',
    description:
      'Every one of these started as a prototype. A cross-section of the kind of software this process produces.',
    live: 'Live',
    building: 'In progress',
    open: 'View',
    prev: 'Previous product',
    next: 'Next product',
  },
};

const NavButton = ({ onClick, children, label, color }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="w-12 h-12 rounded-full border flex items-center justify-center transition-colors duration-300"
    style={{ borderColor: color, color }}
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = color; e.currentTarget.style.color = '#fff'; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = color; }}
  >
    {children}
  </button>
);

const Thumb = ({ app, index, isActive, color, onSelect, innerRef }) => (
  <button
    ref={innerRef}
    type="button"
    onClick={() => onSelect(index)}
    aria-label={app.title}
    aria-current={isActive}
    className="group relative shrink-0 w-28 md:w-36 text-left transition-opacity duration-500"
    style={{ opacity: isActive ? 1 : 0.42 }}
  >
    <span
      className="block relative overflow-hidden rounded-md aspect-[4/3] border transition-colors duration-500"
      style={{ borderColor: isActive ? color : 'var(--border-color)' }}
    >
      <img
        src={app.image}
        alt=""
        loading="lazy"
        width={288}
        height={216}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        draggable={false}
      />
    </span>
    <span className="mt-2 flex items-center gap-2">
      <span className="font-mono-swiss text-[9px] tracking-[0.2em] tabular-nums" style={{ color: isActive ? color : 'inherit' }}>
        {String(index + 1).padStart(2, '0')}
      </span>
      <span className="font-mono-swiss text-[9px] tracking-[0.15em] uppercase truncate text-text-muted">
        {app.title}
      </span>
    </span>
  </button>
);

const Cre8AppsGallery = ({ color, lang = 'de' }) => {
  const t = COPY[lang] || COPY.de;
  const apps = CRE8_APPS;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState(1);
  const railRef = useRef(null);
  const thumbRefs = useRef([]);

  const app = apps[index];
  const isLive = app.status === 'live';

  const go = useCallback((next) => {
    setDirection(next > index || (index === apps.length - 1 && next === 0) ? 1 : -1);
    setIndex((next + apps.length) % apps.length);
  }, [index, apps.length]);

  // Taking over stops the reel for good — an auto-advance that resumes under a
  // visitor who is reading is worse than one that never ran.
  const take = useCallback((next) => { setPaused(true); go(next); }, [go]);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setTimeout(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % apps.length);
    }, AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [index, paused, apps.length]);

  // Keep the active thumbnail in view when the stage moves on its own.
  useEffect(() => {
    const el = thumbRefs.current[index];
    const rail = railRef.current;
    if (!el || !rail) return;
    const elBox = el.getBoundingClientRect();
    const railBox = rail.getBoundingClientRect();
    if (elBox.left < railBox.left + 8 || elBox.right > railBox.right - 8) {
      rail.scrollTo({ left: el.offsetLeft - rail.clientWidth / 2 + el.clientWidth / 2, behavior: 'smooth' });
    }
  }, [index]);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onFocusCapture={() => setPaused(true)}
    >
      <div className="max-w-[1800px] mx-auto px-6 md:px-20">
        {/* Heading */}
        <div className="flex items-center gap-3 mb-5">
          <span className="w-8 h-px" style={{ backgroundColor: color }} />
          <span className="font-mono-swiss text-[10px] tracking-[0.3em] uppercase" style={{ color }}>
            {t.label}
          </span>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-20 mb-12 md:mb-16">
          <h3 className="flex-1 mb-0">
            {t.title} <span style={{ color }}>{t.highlight}</span>
          </h3>
          <p className="text-text-muted font-light leading-relaxed max-w-md lg:pb-3">{t.description}</p>
        </div>

        {/* Stage */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-8 lg:gap-14 items-start">
          <div className="relative overflow-hidden rounded-2xl border border-border-color bg-surface-color/40 aspect-[16/10]">
            <AnimatePresence initial={false} mode="popLayout" custom={direction}>
              <Motion.img
                key={app.id}
                src={app.image}
                alt={`CRE8NOW ${app.title} — ${app.subtitle}`}
                custom={direction}
                initial={{ opacity: 0, scale: 1.06, x: direction * 40 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 1.02, x: direction * -40 }}
                transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
            </AnimatePresence>

            <span
              className="absolute top-5 left-5 z-10 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.2em] backdrop-blur-md"
              style={{ backgroundColor: isLive ? color : 'rgba(0,0,0,0.6)', color: '#ffffff' }}
            >
              {isLive ? t.live : t.building}
            </span>

            {/* Oversized index, set into the frame as a graphic mark */}
            <span
              className="absolute bottom-2 right-6 z-10 font-black leading-none tabular-nums audiowide-regular text-[clamp(56px,7vw,104px)] text-white/85 pointer-events-none select-none"
              style={{ textShadow: '0 2px 24px rgba(0,0,0,0.55)' }}
              aria-hidden="true"
            >
              {String(index + 1).padStart(2, '0')}
            </span>

            {/* Autoplay progress — restarts with every slide, hidden once paused */}
            {!paused && (
              <div className="absolute inset-x-0 bottom-0 z-10 h-[3px] bg-black/20">
                <Motion.span
                  key={app.id}
                  className="block h-full origin-left"
                  style={{ backgroundColor: color }}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: AUTOPLAY_MS / 1000, ease: 'linear' }}
                />
              </div>
            )}
          </div>

          {/* Meta */}
          <div className="lg:pt-4">
            <AnimatePresence mode="wait">
              <Motion.div
                key={app.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              >
                <span className="font-mono-swiss text-[10px] tracking-[0.25em] uppercase text-text-muted block mb-4">
                  {app.subtitle}
                </span>
                <h4 className="font-black tracking-tight leading-none mb-4">{app.title}</h4>
                <p className="text-text-muted font-light leading-relaxed mb-6">
                  {app.desc?.[lang] || app.desc?.de}
                </p>
                <div className="flex flex-wrap gap-1.5 mb-8">
                  {(app.tags || []).map((tag) => (
                    <span
                      key={tag}
                      className="font-mono-swiss text-[9px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-[4px] border border-border-color text-text-muted"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                {app.url && (
                  <a
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] border-b pb-1 transition-opacity hover:opacity-60"
                    style={{ color, borderColor: color }}
                  >
                    {t.open} <ExternalLink size={13} />
                  </a>
                )}
              </Motion.div>
            </AnimatePresence>

            <div className="flex items-center gap-4 mt-10">
              <NavButton onClick={() => take(index - 1)} label={t.prev} color={color}>
                <ArrowLeft size={17} />
              </NavButton>
              <NavButton onClick={() => take(index + 1)} label={t.next} color={color}>
                <ArrowRight size={17} />
              </NavButton>
              <span className="font-mono-swiss text-[11px] tracking-[0.25em] tabular-nums text-text-muted ml-2">
                {String(index + 1).padStart(2, '0')}
                <span className="opacity-40"> / {String(apps.length).padStart(2, '0')}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Rail — every product, bleeding off the right edge */}
      <div
        ref={railRef}
        className="flex gap-3 md:gap-4 overflow-x-auto no-scrollbar mt-12 md:mt-16 pl-6 md:pl-20 pr-6 md:pr-20 pb-2"
        style={{ scrollbarWidth: 'none' }}
      >
        {apps.map((item, i) => (
          <Thumb
            key={item.id}
            app={item}
            index={i}
            isActive={i === index}
            color={color}
            onSelect={take}
            innerRef={(el) => { thumbRefs.current[i] = el; }}
          />
        ))}
      </div>
    </div>
  );
};

export default Cre8AppsGallery;
